import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBookingsStaffV1StaffMembersServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/bookings/v1/staff-members',
        destPath: '/v1/staff-members',
      },
      {
        srcPath: '/bookings/v1/bulk/staff-members',
        destPath: '/v1/bulk/staff-members',
      },
      {
        srcPath: '/bookings/staff',
        destPath: '',
      },
      {
        srcPath: '/bookings/v2',
        destPath: '/v2',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/staff-members',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_staff-members';

/**
 * Creates a staff member.
 *
 *
 * By default, the staff member works during the business working hours.
 * You could follow this [sample flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/sample-flows#assign-a-staff-working-hour-schedule) to set custom working hours.
 */
export function createStaffMember(payload: object): RequestOptionsFactory<any> {
  function __createStaffMember({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'staffMember.createdDate' },
          { path: 'staffMember.updatedDate' },
          { path: 'staffMember.mainMedia.image.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.CreateStaffMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createStaffMember;
}

/** Retrieves a staff member. */
export function getStaffMember(payload: object): RequestOptionsFactory<any> {
  function __getStaffMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'GET' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.GetStaffMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/{staffMemberId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getStaffMember;
}

/** Retrieves a deleted staff member from the trash bin. */
export function getDeletedStaffMember(
  payload: object
): RequestOptionsFactory<any> {
  function __getDeletedStaffMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.GetDeletedStaffMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v2/staff-members/trash-bin/{staffMemberId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getDeletedStaffMember;
}

/**
 * Retrieves a list of deleted staff members from the trash bin.
 *
 *
 * List Deleted Staff Members uses these defaults, which you can override:
 * - `paging.limit` set to `100`.
 * - Only staff member fields are returned. Specify the `fields` parameter to include additional data about the staff member's associated resource.
 */
export function listDeletedStaffMembers(
  payload: object
): RequestOptionsFactory<any> {
  function __listDeletedStaffMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'GET' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.ListDeletedStaffMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v2/staff-members/trash-bin',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMembers.createdDate' },
              { path: 'staffMembers.updatedDate' },
              { path: 'staffMembers.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listDeletedStaffMembers;
}

/** Removes a staff member from the trash bin, permanently deleting it. */
export function removeStaffMemberFromTrashBin(
  payload: object
): RequestOptionsFactory<any> {
  function __removeStaffMemberFromTrashBin({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'DELETE' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.RemoveStaffMemberFromTrashBin',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v2/staff-members/trash-bin/{staffMemberId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __removeStaffMemberFromTrashBin;
}

/**
 * Updates a staff member.
 *
 *
 * Each time the staff member is updated, `revision` increments by 1.
 * You must include current revision of the staff member when updating it.
 * This ensures you're working with the latest service information and prevents unintended overwrites.
 */
export function updateStaffMember(payload: object): RequestOptionsFactory<any> {
  function __updateStaffMember({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'staffMember.createdDate' },
          { path: 'staffMember.updatedDate' },
          { path: 'staffMember.mainMedia.image.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'PATCH' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.UpdateStaffMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/{staffMember.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateStaffMember;
}

/**
 * Deletes a staff member.
 *
 *
 * Also deletes the [resource](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction) associated with the staff member.
 */
export function deleteStaffMember(payload: object): RequestOptionsFactory<any> {
  function __deleteStaffMember({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'DELETE' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.DeleteStaffMember',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/{staffMemberId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteStaffMember;
}

/**
 * Creates a query to retrieve a list of staff members.
 *
 * The `queryStaffMembers()` function builds a query to retrieve a list of staff members and returns a `StaffMembersQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/staff-members-query-builder/find) function.
 *
 * You can refine the query by chaining `StaffMembersQueryBuilder` functions onto the query. `StaffMembersQueryBuilder` functions enable you to sort, filter, and control the results that `queryStaffMembers()` returns.
 *
 * `queryStaffMembers()` runs with the following `StaffMembersQueryBuilder` default that you can override:
 *
 * + `limit` is `50`.
 * + Sorted by `id` in ascending order.
 *
 * The functions that are chained to `staffMembersTypes()` are applied in the order they are called.
 *
 * The following `StaffMembersQueryBuilder` functions are supported for the `queryStaffMembers()` function. For a full description of the resource object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/staff-members-query-result/items) property in `StaffMembersQueryResult`.
 */
export function queryStaffMembers(payload: object): RequestOptionsFactory<any> {
  function __queryStaffMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.QueryStaffMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMembers.createdDate' },
              { path: 'staffMembers.updatedDate' },
              { path: 'staffMembers.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryStaffMembers;
}

/**
 * Counts how many staff members match the given filter.
 *
 *
 * See [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members) for a list of supported filters.
 *
 * ### See also
 *
 * To learn about working with filters in general, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
 */
export function countStaffMembers(payload: object): RequestOptionsFactory<any> {
  function __countStaffMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.CountStaffMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countStaffMembers;
}

/**
 * Connects a Wix user to a staff member, enabling them to manage their own working hour schedule in the dashboard.
 *
 *
 * By default, Wix Bookings uses the staff member's `email`.
 * However, you can specify an alternative email address. If no existing Wix user is associated with that email, Wix sends them an invitation to become a Wix user.
 * If an existing user is found but not linked to the site, Wix Bookings sends an invitation to join the site.
 *
 * To check the connection status, call [Get Staff Member](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/get-staff-member), and specify `ASSOCIATED_IDENTITY_STATUS` in the `fields` parameter.
 *
 * You must call [Disconnect Staff Member From User](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/disconnect-staff-member-from-user) before connecting a different Wix user to the staff member.
 */
export function connectStaffMemberToUser(
  payload: object
): RequestOptionsFactory<any> {
  function __connectStaffMemberToUser({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.ConnectStaffMemberToUser',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath:
          '/v1/staff-members/{staffMemberId}/connect-staff-member-to-user',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __connectStaffMemberToUser;
}

/**
 * Retrieves a list of up to 100 staff members, given the provided filtering, paging, and sorting.
 *
 *
 * ### Defaults
 *
 * Search Staff Members has the following default settings, which you can override:
 * + Sorted by `createdDate` in ascending order.
 * + `cursorPaging.limit` set to `100`.
 *
 * ### See also
 *
 * To learn about working with Search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 */
export function searchStaffMembers(
  payload: object
): RequestOptionsFactory<any> {
  function __searchStaffMembers({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn: 'wix.bookings.staff.v1.StaffMembersService.SearchStaffMembers',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/search',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMembers.createdDate' },
              { path: 'staffMembers.updatedDate' },
              { path: 'staffMembers.mainMedia.image.urlExpirationDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchStaffMembers;
}

/**
 * Disconnects a staff member from a Wix user, clearing the `associatedWixIdentity` field.
 *
 *
 * Consequently, the user can no longer manage the staff member's working hour schedule in the dashboard.
 *
 * You must call Disconnect Staff Member From User before connecting a different Wix user to the staff member.
 */
export function disconnectStaffMemberFromUser(
  payload: object
): RequestOptionsFactory<any> {
  function __disconnectStaffMemberFromUser({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.DisconnectStaffMemberFromUser',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath:
          '/v1/staff-members/{staffMemberId}/disconnect-staff-member-from-user',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __disconnectStaffMemberFromUser;
}

/**
 * Updates a staff member's working hours based on the specified [schedule ID](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction).
 *
 *
 * ### Default working hours
 *
 * By default, staff members work during the opening hours of the business's default [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
 *
 * ### Schedule ID
 *
 * You can specify either the ID of the business's working hour schedule or the  staff member's event schedule.
 * The call fails if you specify a different schedule ID.
 *
 * #### Staff event schedule
 *
 * To customize a staff member's working hours, specify their event schedule ID as `scheduleId`.
 * Refer to this [sample flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/sample-flows#assign-a-staff-working-hour-schedule) for more details.
 *
 * #### Business's working hour schedule
 *
 * To reset a staff member's working hours to the default business hours, specify the ID of the business's working hour schedule as `scheduleId`.
 */
export function assignWorkingHoursSchedule(
  payload: object
): RequestOptionsFactory<any> {
  function __assignWorkingHoursSchedule({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.AssignWorkingHoursSchedule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath:
          '/v1/staff-members/{staffMemberId}/assign-working-hours-schedule',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __assignWorkingHoursSchedule;
}

/**
 * Assigns a custom working hours schedule to the staff member
 *
 *
 * The working hours schedule is a schedule that defines the working hours of a staff member, and dictate when the staff member is available for bookings.
 *
 * By default staff members use the shared business working hours schedule.
 * By assigning a custom working hours schedule to a staff member, you can define specific working hours for that staff member.
 *
 * To create and manage schedules and working hours sessions, use the [Events API](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction).
 * @deprecated It has been replaced with wix.bookings.staff.v1.StaffMembersService.AssignWorkingHoursSchedule(), and will be removed on 2024-12-31.
 */
export function assignCustomSchedule(
  payload: object
): RequestOptionsFactory<any> {
  function __assignCustomSchedule({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.AssignCustomSchedule',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/staff-members/{staffMemberId}/assign-custom-schedule',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'staffMember.createdDate' },
              { path: 'staffMember.updatedDate' },
              { path: 'staffMember.mainMedia.image.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __assignCustomSchedule;
}

/**
 * Synchronously updates [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) for up to 100 staff members.
 *
 *
 * If you specify a tag both in `assignTags` and `unassignTags`, the call succeeds and the tag is assigned.
 */
export function bulkUpdateStaffMemberTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateStaffMemberTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.BulkUpdateStaffMemberTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/bulk/staff-members/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateStaffMemberTags;
}

/**
 * Asynchronously updates [tags](https://dev.wix.com/docs/api-reference/business-management/tags/introduction) for staff members, given the provided filtering.
 *
 *
 * If you specify a tag both in `assignTags` and `unassignTags`, the call succeeds and the tag is assigned.
 *
 * See [Query Staff Members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/query-staff-members) for a list of supported filters.
 */
export function bulkUpdateStaffMemberTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateStaffMemberTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.staff.v1.staff_member',
      method: 'POST' as any,
      methodFqn:
        'wix.bookings.staff.v1.StaffMembersService.BulkUpdateStaffMemberTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBookingsStaffV1StaffMembersServiceUrl({
        protoPath: '/v1/bulk/staff-members/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateStaffMemberTagsByFilter;
}
